import axios from 'src/utils/axios';
import React, { useCallback, useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { Table, Container, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import Spinner from 'src/components/spinner';
import moment from 'moment';
import { AskText, AskConfirm, AskSelect } from 'src/components/questions';
import ImageModal from './ImageModal';

export default function EventProgress() {
	const { id: game_id } = useParams();
	const [event, setEvent] = useState(null);

	const reload = useCallback(async () => {
		try {
			var res = await axios.get(`/game-progress/${game_id}`);
			setEvent(res.data);
		} catch (error) {
			return error;
		}
	}, [game_id]);

	useEffect(() => {
		reload();
	}, [reload]);

	useEffect(() => {
		var interval = setInterval(reload, 10000);
		return () => clearInterval(interval);
	}, [reload]);

	// Saves one field of an entry. Returns an error message (shown in the edit dialog) or nothing on success.
	const updateGTA = async (gte_id, key, value) => {
		try {
			const entry = event.entries.find((e) => e.gte_id === gte_id);
			const updated = { ...entry, [key]: value };
			// The edit dialogs hand back text ("5", "true"); the API needs real numbers/booleans.
			updated.reward_given = Number(updated.reward_given) || 0;
			updated.presentation_worthy = updated.presentation_worthy === true || updated.presentation_worthy === 1 || updated.presentation_worthy === '1' || updated.presentation_worthy === 'true';
			await axios.post(`/game/update/task-entry/${gte_id}`, updated);
			await reload();
		} catch (error) {
			console.error(error);
			return 'Could not save: ' + (error?.response?.data?.error || error.message);
		}
	};

	const history = useHistory();
	const switchGame = (game_id) => {
		history.push(`/game/${game_id}/progress`);
		setEvent(null);
	};

	const [dropdownOpen, setDropdownOpen] = useState(false);
	const toggle = () => setDropdownOpen((prevState) => !prevState);

	// game_id from the URL is a string, the ids from the API are numbers - compare as strings, otherwise
	// the current game is never found and "Next" always jumps to the first game.
	const nextGame = () => {
		const games = event.allGames;
		const i = games.findIndex((g) => String(g.game_id) === String(game_id));
		switchGame(games[(i + 1) % games.length].game_id);
	};

	if (!event) return <Spinner />;
	return (
		<Container>
			<h1>
				Event Progress | {event.game.game_name}
				<Link to={`/event/${event.game.event_id}`} className="btn btn-outline-orange float-right">
					<i className="fa fa-arrow-left"></i> Back to event
				</Link>
			</h1>
			<hr />
			<Dropdown className="float-right" isOpen={dropdownOpen} toggle={toggle}>
				<DropdownToggle caret>Switch Game ({event.allGames.length + ' Total'})</DropdownToggle>
				<DropdownMenu>
					{event.allGames.map((g) => {
						var selected = String(g.game_id) === String(game_id) ? '(Selected)' : '';
						return (
							<DropdownItem key={g.game_id} onClick={() => switchGame(g.game_id)}>
								{g.game_id} - {g.game_name} {selected}
							</DropdownItem>
						);
					})}
				</DropdownMenu>
				<Button onClick={() => nextGame()} className="ml-1" style={{ display: 'inline' }}>
					Next
				</Button>
			</Dropdown>
			<h4>
				Total Score:&nbsp;
				{event.game.total_score}
			</h4>

			<Table bordered striped>
				<thead>
					<tr>
						<th>Task Name</th>
						<th>Submitted Time</th>
						<th>Answers</th>
						<th>Their Answer</th>
						<th>Score</th>
						<th>Slideshow</th>
					</tr>
				</thead>
				<tbody>
					{event.entries.map((e) => {
						return (
							<tr key={e.gte_id}>
								<th>{e.task_title}</th>
								<td>{moment.unix(e.submitted_timestamp).fromNow()}</td>
								<td>
									<small>
										{e.answers &&
											e.answers.map(({ answer_value }, index) => {
												if (e.answers.length === index + 1) return answer_value;
												return answer_value + ' | ';
											})}
									</small>
								</td>
								<td>
									{e.entry_image ? (
										<ImageModal src={e.entry_image}>
											<img style={{ maxWidth: '100px', maxHeight: '100px' }} src={e.entry_image} alt="" />
										</ImageModal>
									) : (
										<>
											{e.task_answer} &nbsp;
											<AskText value={e.task_answer} onSubmit={async (v) => await updateGTA(e.gte_id, 'task_answer', v)} />
										</>
									)}
								</td>
								<td>
									{e.reward_given} &nbsp;
									<AskText value={e.reward_given} type="number" step="1" onSubmit={async (v) => await updateGTA(e.gte_id, 'reward_given', v)} />
								</td>
								<td>
									{e.presentation_worthy ? 'YES' : 'NO'} &nbsp;
										<AskSelect
										value={e.presentation_worthy ? '1' : '0'}
										values={[
											{ value: '0', text: 'NO' },
											{ value: '1', text: 'YES' }
										]}
										required
										onSubmit={async (v) => await updateGTA(e.gte_id, 'presentation_worthy', v)}
										title="Presentation Worthy?"
										label="Yes / No? (1/0)"
									></AskSelect>
								</td>
							</tr>
						);
					})}
				</tbody>
			</Table>

			<ClueTable clues={event.clues} reload={reload} />
		</Container>
	);
}

const ClueTable = ({ clues, reload }) => {
	const { id: game_id } = useParams();

	const giveClue = async (clue_id) => {
		try {
			await axios.post(`/game-progress/${game_id}/give-clue`, {
				clue_id
			});
			await reload();
		} catch (error) {
			console.error(error);
			return 'There was a server error';
		}
	};

	return (
		<Table className="mt-5" bordered striped>
			<thead>
				<tr>
					<th>Clue</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{clues.map((c) => {
					return (
						<tr key={c.clue_id}>
							<td>{c.value}</td>
							<td>
								{c.game_clue_id ? (
									'Given'
								) : (
									<AskConfirm description="Give clue?" onSubmit={() => giveClue(c.clue_id)}>
										<Button size="sm">Give Clue</Button>
									</AskConfirm>
								)}
							</td>
						</tr>
					);
				})}
			</tbody>
		</Table>
	);
};
